// @ts-ignore
import nspell from 'nspell';
// @ts-ignore
import aff from './dict/en.aff?raw';
// @ts-ignore
import dic from './dict/en.dic?raw';

let spellchecker: any = null;

function getSpellchecker() {
  if (!spellchecker && aff && dic) {
    spellchecker = nspell(aff, dic);
  }
  return spellchecker;
}

export function getSpellingSuggestions(word: string): string[] {
  const checker = getSpellchecker();
  if (!checker) return [];
  return checker.suggest(word);
}

export interface GrammarIssue {
  start: number;
  end: number;
  text: string;
  suggestion: string;
  suggestions?: string[];
  type: 'spelling' | 'grammar' | 'style' | 'continuity';
  message: string;
}

export interface Suggestion {
  text: string;
  trigger: string;
  type: 'completion' | 'correction' | 'link';
}


// Common misspellings and corrections
const commonMisspellings: Record<string, string> = {
  'teh': 'the',
  'adn': 'and',
  'taht': 'that',
  'wiht': 'with',
  'fo': 'of',
  'ot': 'to',
  'si': 'is',
  'ti': 'it',
  'na': 'an',
  'hwat': 'what',
  'wen': 'when',
  'wer': 'where',
  'wo': 'who',
  'wy': 'why',
  'hwy': 'why',
  'becuase': 'because',
  'becasue': 'because',
  'thier': 'their',
  'theri': 'their',
  'theyre': "they're",
  'youre': "you're",
  'its': "it's",
  'dont': "don't",
  'wont': "won't",
  'cant': "can't",
  'isnt': "isn't",
  'wasnt': "wasn't",
  'werent': "weren't",
  'hasnt': "hasn't",
  'havent': "haven't",
  'hadnt': "hadn't",
  'wouldnt': "wouldn't",
  'couldnt': "couldn't",
  'shouldnt': "shouldn't",
};

// Common word completions based on context
const contextualCompletions: Record<string, string[]> = {
  'the': ['old', 'young', 'dark', 'bright', 'ancient', 'mysterious'],
  'a': ['small', 'large', 'strange', 'familiar', 'distant'],
  'he': ['looked', 'walked', 'stood', 'sat', 'waited', 'wondered'],
  'she': ['looked', 'walked', 'stood', 'sat', 'waited', 'wondered'],
  'they': ['walked', 'stood', 'waited', 'watched', 'began'],
  'it': ['was', 'seemed', 'appeared', 'felt', 'looked'],
  'marcus': ['approached', 'looked', 'wondered', 'felt', 'reached'],
  'castle': ['stood', 'loomed', 'appeared', 'waited'],
  'dagger': ['hummed', 'glowed', 'pulsed', 'vibrated'],
  'blade': ['gleamed', 'shone', 'cut', 'sliced'],
};

// Style improvements
const styleImprovements: Array<{ pattern: RegExp; suggestion: string; message: string }> = [
  { pattern: /\bvery\s+(\w+)/gi, suggestion: '$1', message: 'Consider removing "very" for stronger prose' },
  { pattern: /\breally\s+(\w+)/gi, suggestion: '$1', message: 'Consider removing "really" for stronger prose' },
  { pattern: /\bsuddenly\b/gi, suggestion: '', message: '"Suddenly" can weaken the impact of action' },
  { pattern: /\bjust\b/gi, suggestion: '', message: 'Consider if "just" is necessary' },
  { pattern: /\bquite\b/gi, suggestion: '', message: 'Consider removing "quite"' },
  { pattern: /\brather\b/gi, suggestion: '', message: 'Consider removing "rather"' },
  { pattern: /\bactually\b/gi, suggestion: '', message: 'Consider removing "actually"' },
  { pattern: /\bliterally\b/gi, suggestion: '', message: 'Consider removing "literally"' },
  { pattern: /\bbasically\b/gi, suggestion: '', message: 'Consider removing "basically"' },
  { pattern: /\bdefinitely\b/gi, suggestion: '', message: 'Consider removing "definitely"' },
  { pattern: /\bprobably\b/gi, suggestion: '', message: 'Consider removing "probably"' },
  { pattern: /\bmaybe\b/gi, suggestion: '', message: 'Consider removing "maybe"' },
  { pattern: /\bperhaps\b/gi, suggestion: '', message: 'Consider removing "perhaps"' },
  { pattern: /\bsomewhat\b/gi, suggestion: '', message: 'Consider removing "somewhat"' },
  { pattern: /\bkind\s+of\b/gi, suggestion: '', message: 'Consider removing "kind of"' },
  { pattern: /\bsort\s+of\b/gi, suggestion: '', message: 'Consider removing "sort of"' },
];

// Passive voice detection (simplified)
const passiveVoicePatterns = [
  /\bwas\s+\w+ed\b/gi,
  /\bwere\s+\w+ed\b/gi,
  /\bbeen\s+\w+ed\b/gi,
  /\bbeing\s+\w+ed\b/gi,
];

// Grammar Rules (Pattern matching for common errors)
const grammarRules: Array<{ pattern: RegExp; suggestion: string; message: string; type: 'grammar' }> = [
  { pattern: /\b(they|we|you)\s+is\b/gi, suggestion: '$1 are', message: 'Subject-verb agreement error', type: 'grammar' },
  { pattern: /\b(he|she|it)\s+are\b/gi, suggestion: '$1 is', message: 'Subject-verb agreement error', type: 'grammar' },
  { pattern: /\b(i)\s+is\b/gi, suggestion: 'I am', message: 'Subject-verb agreement error', type: 'grammar' },
  { pattern: /\ba\s+([aeiou]\w+)/gi, suggestion: 'an $1', message: 'Use "an" before a vowel sound', type: 'grammar' },
  { pattern: /\ban\s+([^aeiou]\w+)/gi, suggestion: 'a $1', message: 'Use "a" before a consonant sound', type: 'grammar' },
  { pattern: /\byour\s+(so|very|really|quite)\b/gi, suggestion: "you're $1", message: 'Did you mean "you\'re"?', type: 'grammar' },
  { pattern: /\byour\s+(a|an|the)\b/gi, suggestion: "you're $1", message: 'Did you mean "you\'re"?', type: 'grammar' },
];

// Repetitive words detection (exported for potential future use)
export function findRepetitiveWords(text: string, threshold: number = 3): Array<{ word: string; positions: number[] }> {
  const words = text.toLowerCase().match(/\b\w{4,}\b/g) || [];
  const wordCounts: Record<string, number[]> = {};

  let position = 0;
  words.forEach(word => {
    if (!wordCounts[word]) wordCounts[word] = [];
    wordCounts[word].push(position);
    position += word.length + 1;
  });

  return Object.entries(wordCounts)
    .filter(([_, positions]) => positions.length >= threshold)
    .map(([word, positions]) => ({ word, positions }));
}

// Check for continuity issues (same word used differently)
export function checkContinuity(text: string, knownTerms: string[]): GrammarIssue[] {
  const issues: GrammarIssue[] = [];

  // Check for terms that might be confused
  knownTerms.forEach(term => {
    const pattern = new RegExp(`\\b${term}\\b`, 'gi');
    const matches = Array.from(text.matchAll(pattern));
    // This is a simplified check - in a real app, you'd compare with previous usages
    // and flag if the same term is used inconsistently
    if (matches.length > 1) {
      // Could flag potential continuity issues here
    }
  });

  return issues;
}

// Main grammar check function
export function checkGrammar(
  text: string,
  knownBubbles: { id: string; name: string; type: string }[] = [],
  customWords: string[] = []
): GrammarIssue[] {
  const issues: GrammarIssue[] = [];
  const wordsWithOffsets: { word: string; offset: number }[] = [];

  // Custom tokenizer for precise offsets
  const regex = /\b\w+\b/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    wordsWithOffsets.push({ word: match[0], offset: match.index });
  }

  wordsWithOffsets.forEach(({ word, offset }) => {
    const lowerWord = word.toLowerCase();

    // 1. Direct dictionary check (Manual overrides for speed or common typos)
    if (commonMisspellings[lowerWord]) {
      issues.push({
        start: offset,
        end: offset + word.length,
        text: word,
        suggestion: commonMisspellings[lowerWord],
        type: 'spelling',
        message: `Did you mean "${commonMisspellings[lowerWord]}"?`,
      });
      return;
    }

    const checker = getSpellchecker();
    if (checker) {
      const isCorrect = checker.correct(word) ||
        knownBubbles.some(b => b.name.toLowerCase().includes(lowerWord)) ||
        customWords.some(cw => cw.toLowerCase() === lowerWord);

      if (!isCorrect) {
        const suggestions = checker.suggest(word);
        if (suggestions.length > 0) {
          issues.push({
            start: offset,
            end: offset + word.length,
            text: word,
            suggestion: suggestions[0],
            suggestions: suggestions.slice(0, 5),
            type: 'spelling',
            message: `Possible misspelling. Did you mean "${suggestions[0]}"?`,
          });
        }
      }
    }
  });

  // 3. Style issues
  styleImprovements.forEach(({ pattern, suggestion, message }) => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      if (match.index !== undefined) {
        issues.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          suggestion: suggestion || match[0],
          type: 'style',
          message,
        });
      }
    }
  });

  // 4. Grammar Rules
  grammarRules.forEach(({ pattern, suggestion, message, type }) => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      if (match.index !== undefined) {
        issues.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          suggestion: match[0].replace(pattern, suggestion),
          type,
          message,
        });
      }
    }
  });

  // 5. Passive voice
  passiveVoicePatterns.forEach(pattern => {
    const matches = text.matchAll(pattern);
    for (const match of matches) {
      if (match.index !== undefined) {
        issues.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          suggestion: match[0],
          type: 'grammar',
          message: 'Consider using active voice for stronger prose',
        });
      }
    }
  });

  return issues;
}

// Get contextual suggestions
export function getSuggestions(text: string, cursorPosition: number): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Get the last word before cursor
  const textBeforeCursor = text.slice(0, cursorPosition);
  const words = textBeforeCursor.match(/\b\w+\b/g) || [];
  const lastWord = words[words.length - 1]?.toLowerCase() || '';

  // Check for contextual completions
  if (contextualCompletions[lastWord]) {
    contextualCompletions[lastWord].forEach(completion => {
      suggestions.push({
        text: ` ${completion}`,
        trigger: lastWord,
        type: 'completion',
      });
    });
  }

  // Check for sentence starters
  const lastChar = textBeforeCursor.slice(-1);
  if (lastChar === '.' || lastChar === '!' || lastChar === '?' || textBeforeCursor.trim() === '') {
    const sentenceStarters = ['The', 'He', 'She', 'It', 'They', 'A', 'In', 'With', 'As'];
    sentenceStarters.forEach(starter => {
      suggestions.push({
        text: ` ${starter}`,
        trigger: '',
        type: 'completion',
      });
    });
  }

  return suggestions.slice(0, 3); // Limit to 3 suggestions
}

// Check if text might be referring to a known bubble
export function findPotentialLinks(text: string, bubbles: { id: string; name: string; type: string }[]): Array<{ text: string; bubbleId: string; bubbleName: string; confidence: number }> {
  const potentialLinks: Array<{ text: string; bubbleId: string; bubbleName: string; confidence: number }> = [];

  bubbles.forEach(bubble => {
    const name = bubble.name;
    const nameLower = name.toLowerCase();

    // Direct match
    const directPattern = new RegExp(`\\b${name}\\b`, 'gi');
    const directMatches = text.matchAll(directPattern);
    for (const match of directMatches) {
      potentialLinks.push({
        text: match[0],
        bubbleId: bubble.id,
        bubbleName: name,
        confidence: 1.0,
      });
    }

    // Partial matches (e.g., "the blade" when bubble is "The Dagger")
    const words = nameLower.split(/\s+/);
    words.forEach(word => {
      if (word.length > 3 && word !== 'the' && word !== 'and') {
        const partialPattern = new RegExp(`\\b${word}\\b`, 'gi');
        const partialMatches = text.matchAll(partialPattern);
        for (const match of partialMatches) {
          // Check if this word is already a direct match
          const isDirectMatch = potentialLinks.some(link =>
            link.text.toLowerCase() === match[0].toLowerCase() && link.bubbleId === bubble.id
          );
          if (!isDirectMatch) {
            potentialLinks.push({
              text: match[0],
              bubbleId: bubble.id,
              bubbleName: name,
              confidence: 0.5,
            });
          }
        }
      }
    });
  });

  // Remove duplicates, keeping highest confidence
  const uniqueLinks = new Map<string, typeof potentialLinks[0]>();
  potentialLinks.forEach(link => {
    const key = `${link.text}-${link.bubbleId}`;
    const existing = uniqueLinks.get(key);
    if (!existing || existing.confidence < link.confidence) {
      uniqueLinks.set(key, link);
    }
  });

  return Array.from(uniqueLinks.values());
}

// Get word count statistics
export function getWordStats(text: string): {
  totalWords: number;
  uniqueWords: number;
  averageWordLength: number;
  sentenceCount: number;
  paragraphCount: number;
} {
  const words = text.match(/\b\w+\b/g) || [];
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [];
  const paragraphs = text.split(/\n\s*\n/).filter(p => p.trim().length > 0);

  const uniqueWords = new Set(words.map(w => w.toLowerCase()));
  const totalLength = words.reduce((sum, w) => sum + w.length, 0);

  return {
    totalWords: words.length,
    uniqueWords: uniqueWords.size,
    averageWordLength: words.length > 0 ? totalLength / words.length : 0,
    sentenceCount: sentences.length,
    paragraphCount: paragraphs.length,
  };
}
